import { CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CustomScoringOptionProps {
  option: {
    option_id?: string;
    option_text_en: string;
    option_text_fr?: string;
    option_image_en?: string | null;
    option_image_fr?: string | null;
    third_image_en?: string | null;
    third_image_fr?: string | null;
    is_correct: boolean;
  };
  language?: 'en' | 'fr';
  onMarkCorrect?: (optionId: string, isCorrect: boolean) => void;
}

/**
 * CustomScoringOption - Manual scoring only
 * Displays an option with ability to mark as correct/incorrect.
 * Used for custom question types in live scoring (no feed stats, no auto-grading).
 */
export function CustomScoringOption({
  option,
  language = 'en',
  onMarkCorrect,
}: CustomScoringOptionProps) {
  const optionText =
    language === 'en' ? option.option_text_en : option.option_text_fr || option.option_text_en;
  const optionImage =
    language === 'en' ? option.option_image_en : option.option_image_fr || option.option_image_en;
  const thirdImage =
    language === 'en' ? option.third_image_en : option.third_image_fr || option.third_image_en;

  const isCorrect = option.is_correct;

  const handleMarkCorrect = () => {
    if (option.option_id && onMarkCorrect) {
      onMarkCorrect(option.option_id, true);
    }
  };

  const handleMarkIncorrect = () => {
    if (option.option_id && onMarkCorrect) {
      onMarkCorrect(option.option_id, false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-2 flex-1">
      {/* Option Card */}
      <div
        className={`relative w-full p-6 rounded-lg border transition-colors flex flex-col items-center justify-center min-h-[140px] ${
          isCorrect ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
        }`}
      >
        {/* Checkmark / Incorrect Icon - Top Right */}
        {isCorrect ? (
          <div className="absolute top-2 right-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
        ) : null}

        {/* Option Image */}
        {(optionImage || thirdImage) && (
          <div className="mb-3">
            {optionImage && (
              <img
                src={optionImage}
                alt={optionText}
                className="h-16 w-16 object-contain rounded-full"
              />
            )}
            {thirdImage && !optionImage && (
              <img src={thirdImage} alt="" className="h-16 w-16 object-contain rounded-full" />
            )}
          </div>
        )}

        {/* Option Text */}
        <div className="text-center">
          <span className={`text-sm font-medium ${isCorrect ? 'text-gray-900' : 'text-gray-700'}`}>
            {optionText}
          </span>
        </div>
      </div>

      {/* Mark Correct / Incorrect Button */}
      {onMarkCorrect && option.option_id && (
        <div className="flex justify-center w-full">
          {isCorrect ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkIncorrect}
              className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
              data-testid={`mark-incorrect-${option.option_id}`}
            >
              <XCircle className="mr-1 h-4 w-4" />
              Mark Incorrect
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkCorrect}
              className="text-green-600 border-green-200 hover:bg-green-50 hover:border-green-300"
              data-testid={`mark-correct-${option.option_id}`}
            >
              <CheckCircle2 className="mr-1 h-4 w-4" />
              Mark Correct
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
